// packages/web/src/components/auth/LoginForm.tsx
// FAANG-GRADE: Login form for /login page
// Uses shared LoginFormInner for identical login logic
// Preserves page load cleanup and auto-submit for harness

import { Box, Link } from '@mui/material';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import LoginFormInner from './LoginFormInner';
import { logout } from '../../store/slices/authSlice';

const LoginForm: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch();

  const catalogSlug = (router.query.catalogSlug || router.query.catalogInstance) as string | null;
  const isCatalogLogin = !!catalogSlug;

  // Best-effort cleanup when landing on /login so tests/users start clean.
  function clearCookie(name: string) {
    try {
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const parts = host ? host.split('.') : [];
      const domains = [''].concat(parts.map((_, i) => '.' + parts.slice(i).join('.')));
      const paths = ['/', '/api'];
      domains.forEach((d) =>
        paths.forEach((p) => {
          document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}${d ? `; domain=${d}` : ''}`;
        }),
      );
    } catch (e) {
      // Cookie cleanup may fail if cookies don't exist
    }
  }

  useEffect(() => {
    // Only run cleanup on main /login page, not on catalog login pages
    if (isCatalogLogin) {
      console.log('[LOGIN_DEBUG][LoginForm] Catalog login page - skipping cleanup');
      return;
    }

    console.log('[LOGIN_DEBUG][LoginForm] ===== PAGE LOAD CLEANUP =====');
    console.log('[LOGIN_DEBUG][LoginForm] Clearing auth state on login page load');

    if (typeof window === 'undefined') return;
    const names = [
      'faang_token',
      'token',
      'authToken',
      'accessToken',
      'jwt',
      'session',
      'connect.sid',
    ];
    names.forEach(clearCookie);
    try {
      // Clear ALL auth-related localStorage keys including subdomain to prevent cached subdomain pollution
      ['token', 'authToken', 'accessToken', 'jwt', 'user', 'tenantId', 'subdomain'].forEach((k) =>
        window.localStorage.removeItem(k),
      );
      sessionStorage.clear();

      // Clear Redux state to prevent CartContext from trying to load with cleared tokens
      dispatch(logout());

      // Additional cleanup: clear any JWT tokens that contain subdomain claims
      // This prevents the axios interceptor from sending X-Subdomain headers for customer logins
      const host = window.location.hostname;
      if (host.includes('visionarynow.com')) {
        const cookies = document.cookie.split(';');
        cookies.forEach((cookie) => {
          const [name] = cookie.trim().split('=');
          if (name && (name.includes('token') || name.includes('jwt'))) {
            clearCookie(name.trim());
          }
        });
      }

      console.log('[LOGIN_DEBUG][LoginForm] Cleanup completed');
      console.log('[LOGIN_DEBUG][LoginForm] ===== CLEANUP COMPLETE =====');
    } catch (e) {
      // Cleanup errors are non-critical
    }
  }, [isCatalogLogin, dispatch]);

  return (
    <React.Fragment>
      <LoginFormInner
        catalogSlug={catalogSlug}
        isCatalogLogin={isCatalogLogin}
        showTitle={false}
        showLinks={false}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
        <Link component={NextLink} href="/forgot-password" variant="body2">
          Forgot password?
        </Link>
        <Link
          component={NextLink}
          href={catalogSlug ? `/c/${catalogSlug}/register` : '/register'}
          variant="body2"
        >
          {"Don't have an account? Sign Up"}
        </Link>
      </Box>
    </React.Fragment>
  );
};

export default LoginForm;
